import { CAMPAIGN_CONST } from "@/config/constants";

type RecommendationId = string | number | null | undefined;

type BudgetRecommendationChannel = {
  fk_channel_id?: RecommendationId;
  creative_id?: RecommendationId;
  sys_creative_id?: RecommendationId;
};

type BudgetRecommendationCreativePackage = {
  creative_id?: RecommendationId;
  sys_creative_id?: RecommendationId;
  package_channels?: BudgetRecommendationChannel[] | null;
};

type BudgetRecommendationLocation = {
  id?: RecommendationId;
  system_generated_location_id?: RecommendationId;
  targeting_type?: string | null;
  creatives?: BudgetRecommendationCreativePackage[] | null;
};

type BudgetRecommendationCampaign = {
  id?: RecommendationId;
  system_generated_campaign_id?: RecommendationId;
  budget_type?: string | null;
  is_budget_recommendation?: unknown;
};

type BudgetRecommendationBrand = {
  id?: RecommendationId;
  system_generated_brand_id?: RecommendationId;
  is_budget_recommendation?: unknown;
};

type BudgetRecommendationLocationFormData = {
  start_date?: string | null;
  end_date?: string | null;
  creatives?: RecommendationId[] | null;
  channels?: RecommendationId[] | null;
  budget?: RecommendationId;
  budgetRecommendation?: BudgetRecommendationResponse | null;
  budgetRecommendationKey?: string | null;
  budgetRecommendationsByKey?: Record<string, BudgetRecommendationResponse>;
};

type BudgetRecommendationResponse = {
  finalRecommendation?: RecommendationId;
  recommendationId?: RecommendationId;
  confidenceSignal?: string | null;
  explanation?: string | null;
  [key: string]: unknown;
};

type BudgetRecommendationApiResponse = {
  data?: unknown;
  [key: string]: unknown;
};

type BudgetRecommendationBulkLocation = {
  loc: BudgetRecommendationLocation;
  locFormData: BudgetRecommendationLocationFormData;
};

function normalizeRecommendationId(value: RecommendationId) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && String(value).trim() !== ""
    ? numericValue
    : value;
}

function uniqueValues<T>(values: T[] = []) {
  return [
    ...new Set(values.filter((value) => value !== undefined && value !== null)),
  ];
}

function normalizeKeyValues(values: RecommendationId[] | null | undefined) {
  return (values || []).map((value) => String(value)).sort();
}

function isSameBudget(
  budget: RecommendationId,
  recommendationBudget: RecommendationId
) {
  return (
    budget !== undefined &&
    budget !== null &&
    recommendationBudget !== undefined &&
    recommendationBudget !== null &&
    Number(budget) === Number(recommendationBudget)
  );
}

export function isBudgetRecommendationEnabled({
  campaign,
}: {
  campaign?: BudgetRecommendationCampaign | null;
  selectedBrand?: BudgetRecommendationBrand | null;
}) {
  return (
    campaign?.budget_type === CAMPAIGN_CONST.BUDGET_TYPE.LOCATION_SPECIFIC &&
    campaign?.is_budget_recommendation === 1
  );
}

export function buildBudgetRecommendationSelectionKey(
  locFormData: BudgetRecommendationLocationFormData = {}
) {
  return JSON.stringify({
    startDate: locFormData.start_date || "",
    endDate: locFormData.end_date || "",
    creativeIds: normalizeKeyValues(locFormData.creatives),
    channelIds: normalizeKeyValues(locFormData.channels),
  });
}

export function buildBudgetRecommendationState({
  locFormData = {},
  recommendation,
}: {
  locFormData: BudgetRecommendationLocationFormData;
  recommendation: BudgetRecommendationResponse;
}) {
  const recommendationKey = buildBudgetRecommendationSelectionKey(locFormData);

  return {
    budgetRecommendation: isSameBudget(
      locFormData.budget,
      recommendation?.finalRecommendation
    )
      ? recommendation
      : undefined,
    budgetRecommendationKey: recommendationKey,
    budgetRecommendationsByKey: {
      ...(locFormData.budgetRecommendationsByKey || {}),
      [recommendationKey]: recommendation,
    },
  };
}

export function getStoredBudgetRecommendationForSelection(
  locFormData: BudgetRecommendationLocationFormData = {}
) {
  const recommendationKey = buildBudgetRecommendationSelectionKey(locFormData);
  const recommendationFromMap =
    locFormData.budgetRecommendationsByKey?.[recommendationKey];

  if (recommendationFromMap) {
    return recommendationFromMap;
  }

  if (
    locFormData.budgetRecommendation &&
    (!locFormData.budgetRecommendationKey ||
      locFormData.budgetRecommendationKey === recommendationKey)
  ) {
    return locFormData.budgetRecommendation;
  }

  return undefined;
}

export function getBudgetRecommendationForBudget(
  enteredBudget: RecommendationId,
  locFormData: BudgetRecommendationLocationFormData = {}
) {
  const recommendation = getStoredBudgetRecommendationForSelection(locFormData);

  return isSameBudget(enteredBudget, recommendation?.finalRecommendation)
    ? recommendation
    : undefined;
}

export function getBudgetRecommendationSubmitData(
  locFormData: BudgetRecommendationLocationFormData = {}
) {
  const recommendation = getStoredBudgetRecommendationForSelection(locFormData);

  if (!recommendation) {
    return undefined;
  }

  return {
    ...recommendation,
    selectionKey: buildBudgetRecommendationSelectionKey(locFormData),
    participantAccepted: isSameBudget(
      locFormData.budget,
      recommendation.finalRecommendation
    ),
    participantFinalBudget: locFormData.budget,
  };
}

export function getBudgetRecommendationResponseData(response: unknown) {
  const apiResponse = response as BudgetRecommendationApiResponse;
  const responseData = apiResponse?.data as BudgetRecommendationApiResponse;

  return responseData?.data || responseData || response;
}

export function getBulkBudgetRecommendationJobId(response: unknown) {
  const data = getBudgetRecommendationResponseData(
    response
  ) as BudgetRecommendationApiResponse;

  return (
    data?.batchId || data?.batch_id || data?.jobId || data?.job_id || data?.id
  );
}

export function getSelectedChannelsForRecommendation(
  loc: BudgetRecommendationLocation = {},
  locFormData: BudgetRecommendationLocationFormData = {}
) {
  const selectedPackageIds = new Set(
    (locFormData.creatives || []).map((creativeId) => String(creativeId))
  );
  const selectedFormChannelIds = new Set(
    (locFormData.channels || []).map((channelId) => String(channelId))
  );

  return uniqueValues(
    (loc.creatives || []).flatMap((creativePackage) => {
      if (!selectedPackageIds.has(String(creativePackage.creative_id))) {
        return [];
      }

      return (creativePackage.package_channels || []).flatMap((channel) => {
        const channelId = normalizeRecommendationId(channel.fk_channel_id);

        if (
          selectedFormChannelIds.size &&
          !selectedFormChannelIds.has(String(channelId))
        ) {
          return [];
        }

        return [channelId];
      });
    })
  );
}

export function getSelectedCreativesForRecommendation(
  loc: BudgetRecommendationLocation = {},
  locFormData: BudgetRecommendationLocationFormData = {}
) {
  const selectedChannels = getSelectedChannelsForRecommendation(
    loc,
    locFormData
  );
  const selectedChannelIds = new Set(
    selectedChannels.map((channelId) => String(channelId))
  );
  const selectedPackageIds = new Set(
    (locFormData.creatives || []).map((creativeId) => String(creativeId))
  );
  const creativeIdsByChannel = new Map<RecommendationId, RecommendationId[]>();

  (loc.creatives || []).forEach((creativePackage) => {
    if (!selectedPackageIds.has(String(creativePackage.creative_id))) {
      return;
    }

    (creativePackage.package_channels || []).forEach((channel) => {
      const channelId = normalizeRecommendationId(channel.fk_channel_id);

      if (!selectedChannelIds.has(String(channelId))) {
        return;
      }

      const creativeId =
        channel.sys_creative_id ||
        channel.creative_id ||
        creativePackage.sys_creative_id ||
        creativePackage.creative_id;
      const existingCreativeIds = creativeIdsByChannel.get(channelId) || [];

      creativeIdsByChannel.set(
        channelId,
        uniqueValues([...existingCreativeIds, creativeId])
      );
    });
  });

  return selectedChannels
    .map((channelId) => ({
      channelId,
      creativeIds: creativeIdsByChannel.get(channelId) || [],
    }))
    .filter(({ creativeIds }) => creativeIds.length > 0);
}

export function buildBudgetRecommendationRequest({
  campaign,
  selectedBrand,
  loc,
  locFormData,
}: {
  campaign: BudgetRecommendationCampaign;
  selectedBrand: BudgetRecommendationBrand;
  loc: BudgetRecommendationLocation;
  locFormData: BudgetRecommendationLocationFormData;
}) {
  return {
    locationId: loc?.system_generated_location_id || loc?.id,
    targetting_type: loc?.targeting_type || null,
    campaignId: campaign?.system_generated_campaign_id || campaign?.id,
    brandId: selectedBrand?.id,
    flightStartDate: locFormData?.start_date,
    flightEndDate: locFormData?.end_date,
    channels: getSelectedChannelsForRecommendation(loc, locFormData),
    creatives: getSelectedCreativesForRecommendation(loc, locFormData),
  };
}

export function buildBulkBudgetRecommendationRequest({
  campaign,
  selectedBrand,
  locations = [],
}: {
  campaign: BudgetRecommendationCampaign;
  selectedBrand: BudgetRecommendationBrand;
  locations: BudgetRecommendationBulkLocation[];
}) {
  return {
    campaignId: campaign?.system_generated_campaign_id || campaign?.id,
    brandId: selectedBrand?.id,
    locations: locations.map(({ loc, locFormData }) => ({
      locationId: loc?.system_generated_location_id || loc?.id,
      targetting_type: loc?.targeting_type || null,
      flightStartDate: locFormData?.start_date,
      flightEndDate: locFormData?.end_date,
      channels: getSelectedChannelsForRecommendation(loc, locFormData),
      creatives: getSelectedCreativesForRecommendation(loc, locFormData),
    })),
  };
}
